import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  alpha,
  useTheme,
  CircularProgress,
  TextField,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemText,
  Chip,
  Avatar,
  Stack,
  Badge,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import {
  Today,
  ViewWeek,
  NavigateBefore,
  NavigateNext,
  Search,
  Schedule,
  Person,
  CalendarMonth,
  Engineering,
  Description,
} from "@mui/icons-material";
import { format, parseISO } from "date-fns";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import { url } from "../constants/url";
import axios from "axios";
import { useAuth } from "../context/authContext";
import { COLORS, FONT_FAMILY, TYPOGRAPHY } from "../constants/color";

const SiteCheckCalendar = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { userData, token, hasRole } = useAuth();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  const [view, setView] = useState("timeGridWeek");
  const [projectsData, setProjectsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [calendarApi, setCalendarApi] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [overlapDialog, setOverlapDialog] = useState({
    open: false,
    time: null,
    projects: [],
  });
  const [engineersMap, setEngineersMap] = useState({});

  /** THEME COLORS - Using your custom colors */
  const CALENDAR_COLORS = {
    primary: COLORS.PRIMARY,
    secondary: COLORS.SECONDARY,
    accent: COLORS.LIGHT_SECONDARY,
    background: theme.palette.mode === "light" ? COLORS.BACKGROUND : "#0a1929",
    cardBackground: theme.palette.mode === "light" 
      ? "rgba(255,255,255,0.98)" 
      : "rgba(10, 25, 41, 0.95)",
    border: theme.palette.mode === "light" ? COLORS.BORDER : "rgba(30, 58, 138, 0.6)",
    textPrimary: theme.palette.mode === "light" ? COLORS.TEXT : "#e3f2fd",
    textSecondary: theme.palette.mode === "light" ? COLORS.MUTED_TEXT : "#bbdefb",
  };

  /** Project Colors using your theme */
  const PROJECT_COLORS = [
    COLORS.PRIMARY,
    COLORS.SECONDARY,
    "#421A6E", // Darker purple
    "#652D90", // Your secondary
    "#8A2BE2", // Blue violet
    "#9932CC", // Dark orchid
    "#BA55D3", // Medium orchid
    "#DA70D6", // Orchid
    "#D8BFD8", // Thistle
    "#DDA0DD", // Plum
  ];

  const getProjectColor = (index) => PROJECT_COLORS[index % PROJECT_COLORS.length];

  /** Fetch Engineer Data by ID */
  const fetchEngineerData = async (engineerId) => {
    try {
      // Check cache first
      if (engineersMap[engineerId]) {
        return engineersMap[engineerId];
      }

      const response = await axios.get(`${url}/api/users/fetch`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { userID: engineerId }
      });

      if (response.data?.users && response.data.users.length > 0) {
        const engineer = response.data.users[0];
        // Update cache
        setEngineersMap(prev => ({
          ...prev,
          [engineerId]: engineer
        }));
        return engineer;
      }
      return null;
    } catch (error) {
      console.error(`Error fetching engineer ${engineerId}:`, error);
      return null;
    }
  };

  /** Fetch Projects based on user role */
  const fetchProjectsForCalendar = async () => {
    try {
      setLoading(true);
      
      // Determine which API endpoint to use based on role
      let urlEndpoint = "";
      let params = {
        page: 1,
        limit: 1000,
        sort: 'latest'
      };

      // Add role-specific filters
      if (hasRole("ENGINEER") && userData?._id) {
        // Engineer: show only projects assigned to them
        urlEndpoint = `${url}/api/engineer/assigned-projects/${userData._id}`;
      } else if (hasRole("CLIENT") && userData?._id) {
        // Client: show only their own projects
        urlEndpoint = `${url}/api/client/checklist-requests`;
        params.clientID = userData._id;
      } else if (hasRole("ADMIN") || hasRole("SUPERADMIN")) {
        // Admin/SuperAdmin: show all projects
        urlEndpoint = `${url}/api/admin/checklist-requests`;
      } else {
        setLoading(false);
        return;
      }

      const response = await axios.get(urlEndpoint, {
        headers: { Authorization: `Bearer ${token}` },
        params: params
      });

      // Handle different response structures
      let projects = [];
      if (hasRole("ENGINEER")) {
        projects = response.data?.assignedProjects || [];
      } else {
        projects = response.data?.requests || [];
      }
      
      // Merge FIR requests for clients
      if (hasRole("CLIENT") && userData?._id) {
        try {
          const firResp = await axios.get(`${url}/api/fir/requests`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (firResp.data?.data) {
            projects = [...projects, ...firResp.data.data];
          }
        } catch (e) {
          console.error("Error fetching FIR for client calendar:", e);
        }
      }
      
      // Process projects and fetch engineer data
      const processedProjects = await Promise.all(
        projects
          .filter(project => {
            // Use visitedDate if exists, otherwise use assignedVisitDate or inspectionDateFrom
            const displayDate = project.visitedDate || project.assignedVisitDate || project.inspectionDateFrom;
            return displayDate;
          })
          .map(async (project, index) => {
            // Determine which date to use for calendar
            const displayDate = project.visitedDate 
              ? new Date(project.visitedDate)
              : project.assignedVisitDate 
                ? new Date(project.assignedVisitDate)
                : project.inspectionDateFrom
                  ? new Date(project.inspectionDateFrom)
                  : null;

            // Get engineer info if assigned
            let engineerData = null;
            let engineerName = "Not Assigned";
            
            if (project.assignedEngineer) {
              if (typeof project.assignedEngineer === 'object' && project.assignedEngineer._id) {
                // Already populated
                engineerData = project.assignedEngineer;
                engineerName = project.assignedEngineer.name || "Not Assigned";
              } else {
                // Need to fetch engineer data
                engineerData = await fetchEngineerData(project.assignedEngineer);
                engineerName = engineerData?.name || "Not Assigned";
              }
            } else if (project.assignedEngineers && project.assignedEngineers.length > 0) {
              const eng = project.assignedEngineers[0];
              if (typeof eng === 'object' && eng._id) {
                engineerData = eng;
                engineerName = eng.name || "Not Assigned";
              } else {
                engineerData = await fetchEngineerData(eng);
                engineerName = engineerData?.name || "Not Assigned";
              }
            }

            // Get project name from different sources based on API response
            let projectName = project.nameOfProject || project.projectName || project.propertyName || "N/A";
            let clientName = project.nameOfClient || project.fullName || project.clientID?.name || "N/A";
            let siteCheckType = project.templateTitle || project.propertyType || "Site Check";

            // Truncate long site check type for display
            const truncatedSiteCheckType = siteCheckType.length > 20 
              ? `${siteCheckType.substring(0, 20)}...` 
              : siteCheckType;

            return {
              ...project,
              displayDate: displayDate,
              engineerData: engineerData,
              engineerName: engineerName,
              siteCheckType: siteCheckType,
              truncatedSiteCheckType: truncatedSiteCheckType,
              clientName: clientName,
              projectName: projectName,
              index: index,
              // Original dates for reference
              assignedVisitDate: project.assignedVisitDate ? new Date(project.assignedVisitDate) : null,
              visitedDate: project.visitedDate ? new Date(project.visitedDate) : null,
            };
          })
      );

      // Filter out projects without dates and sort by date
      const filteredProjects = processedProjects
        .filter(project => project.displayDate)
        .sort((a, b) => new Date(a.displayDate) - new Date(b.displayDate));

      setProjectsData(filteredProjects);
      
    } catch (err) {
      console.error("Calendar fetch error:", err);
      setProjectsData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token && userData) {
      fetchProjectsForCalendar();
    }
  }, [token, userData]);

  /** Handle calendar date change */
  const handleDatesSet = (dateInfo) => {
    setCurrentDate(dateInfo.start);
  };

  /** Group projects by exact time for overlapping handling */
  const getGroupedProjectsByTime = useMemo(() => {
    const grouped = {};
    projectsData
      .filter((project) => {
        if (!searchQuery) return true;
        const searchLower = searchQuery.toLowerCase();
        return (
          project.projectName?.toLowerCase()?.includes(searchLower) ||
          project.clientName?.toLowerCase()?.includes(searchLower) ||
          project.siteCheckType?.toLowerCase()?.includes(searchLower) ||
          project.engineerName?.toLowerCase()?.includes(searchLower)
        );
      })
      .forEach((project) => {
        if (!project.displayDate) return;
        
        const start = new Date(project.displayDate);
        // Group by exact time slot (hour and minute)
        const timeKey = start.toISOString().slice(0, 16); // Group by hour and minute
        
        if (!grouped[timeKey]) {
          grouped[timeKey] = [];
        }
        grouped[timeKey].push({ project, start });
      });
    return grouped;
  }, [projectsData, searchQuery]);

  /** Convert Projects -> Events */
  const calendarEvents = useMemo(() => {
    const events = [];
    const MAX_VISIBLE_EVENTS = isMobile ? 2 : isTablet ? 3 : 4; // Responsive max events

    Object.entries(getGroupedProjectsByTime).forEach(([timeKey, projectsAtTime]) => {
      const visibleProjects = projectsAtTime.slice(0, MAX_VISIBLE_EVENTS);
      const hiddenProjects = projectsAtTime.slice(MAX_VISIBLE_EVENTS);

      // Create events for visible projects
      visibleProjects.forEach(({ project, start }) => {
        const end = new Date(start.getTime() + 60 * 60 * 1000);
        
        // Create title with clean text (not HTML)
        const eventTitle = `${project.projectName} • ${project.engineerName} • ${project.truncatedSiteCheckType}`;
        
        // Create extended props for tooltip
        const extendedProps = {
          project, 
          isOverflow: false,
          time: format(start, 'h:mm a'),
          siteCheckType: project.siteCheckType,
          projectName: project.projectName,
          clientName: project.clientName,
          engineerName: project.engineerName,
          truncatedSiteCheckType: project.truncatedSiteCheckType,
          isVisited: !!project.visitedDate,
          displayDate: project.displayDate,
        };

        events.push({
          id: project._id,
          title: eventTitle,
          start,
          end,
          extendedProps: extendedProps,
          backgroundColor: alpha(getProjectColor(project.index), 0.95),
          borderColor: getProjectColor(project.index),
          textColor: "#ffffff",
          display: "block",
          classNames: ["premium-event"],
        });
      });

      // Create +more overflow event
      if (hiddenProjects.length > 0) {
        const firstProject = hiddenProjects[0];
        const start = firstProject.start;
        const end = new Date(start.getTime() + 60 * 60 * 1000);
        
        events.push({
          id: `overflow-${timeKey}`,
          title: `+${hiddenProjects.length} more`,
          start,
          end,
          extendedProps: { 
            isOverflow: true,
            hiddenProjects: hiddenProjects.map(item => item.project),
            time: format(start, 'h:mm a')
          },
          backgroundColor: alpha(COLORS.GREY_COLOR, 0.9),
          borderColor: COLORS.MUTED_TEXT,
          textColor: COLORS.TEXT,
          display: "block",
          classNames: ["overflow-event"],
        });
      }
    });

    return events;
  }, [getGroupedProjectsByTime, isMobile, isTablet]);

  /** Handle event click */
  const handleEventClick = (clickInfo) => {
    const { isOverflow, hiddenProjects, time } = clickInfo.event.extendedProps;
    
    if (isOverflow && hiddenProjects) {
      setOverlapDialog({
        open: true,
        time: time,
        projects: hiddenProjects,
      });
    } else {
      const projectId = clickInfo.event.id;
      navigate(`/projects/${projectId}`);
    }
  };

  /** Format date display based on view */
  const getFormattedDate = () => {
    if (view === 'timeGridDay') {
      return format(currentDate, 'EEEE, MMMM d, yyyy');
    } else {
      return format(currentDate, 'MMMM yyyy');
    }
  };

  /** Get role-specific title */
  const getCalendarTitle = () => {
    if (hasRole("ENGINEER")) return "My Assigned Projects";
    if (hasRole("CLIENT")) return "My Site Check Projects";
    return "All Site Check Projects";
  };

  /** Custom Event Content Renderer - Compact horizontal layout */
  const renderEventContent = (eventInfo) => {
    const { isOverflow, projectName, engineerName, truncatedSiteCheckType, isVisited } = eventInfo.event.extendedProps;
    
    if (isOverflow) {
      return (
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          padding: '0 4px',
        }}>
          <Typography
            sx={{
              fontFamily: FONT_FAMILY,
              fontSize: isMobile ? '0.6rem' : '0.65rem',
              fontWeight: 600,
              color: COLORS.TEXT,
              textAlign: 'center',
              lineHeight: 1,
            }}
          >
            {eventInfo.event.title}
          </Typography>
        </Box>
      );
    }
    
    return (
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        height: '100%',
        padding: '0 4px',
        overflow: 'hidden',
        gap: '4px',
        position: 'relative',
      }}>
        {/* Status Indicator (Text only, no emoji) */}
        <Box sx={{
          position: 'absolute',
          top: 1,
          right: 2,
          fontSize: '0.5rem',
          fontWeight: 600,
          color: 'rgba(255,255,255,0.9)',
          background: isVisited ? COLORS.SUCCESS : COLORS.WARNING,
          borderRadius: '2px',
          padding: '0 2px',
          zIndex: 10,
          minWidth: '14px',
          textAlign: 'center',
          lineHeight: 1.2,
        }}>
          {isVisited ? '✓' : '●'}
        </Box>

        {/* Project Name */}
        <Typography
          sx={{
            fontFamily: FONT_FAMILY,
            fontSize: isMobile ? '0.6rem' : '0.65rem',
            fontWeight: 600,
            color: '#ffffff',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            flexShrink: 1,
            minWidth: '40px',
            lineHeight: 1.2,
          }}
        >
          {projectName}
        </Typography>

        {/* Separator */}
        <Box sx={{
          fontSize: '0.5rem',
          color: 'rgba(255,255,255,0.7)',
          flexShrink: 0,
          lineHeight: 1,
        }}>
          •
        </Box>

        {/* Engineer Name */}
        <Typography
          sx={{
            fontFamily: FONT_FAMILY,
            fontSize: isMobile ? '0.55rem' : '0.6rem',
            fontWeight: 500,
            color: 'rgba(255,255,255,0.9)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            flexShrink: 1,
            minWidth: '35px',
            lineHeight: 1.2,
          }}
        >
          {engineerName}
        </Typography>

        {/* Separator */}
        <Box sx={{
          fontSize: '0.5rem',
          color: 'rgba(255,255,255,0.7)',
          flexShrink: 0,
          lineHeight: 1,
        }}>
          •
        </Box>

        {/* Site Check Type */}
        <Typography
          sx={{
            fontFamily: FONT_FAMILY,
            fontSize: isMobile ? '0.55rem' : '0.6rem',
            fontWeight: 400,
            color: 'rgba(255,255,255,0.8)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            flexShrink: 1,
            minWidth: '35px',
            lineHeight: 1.2,
          }}
        >
          {truncatedSiteCheckType}
        </Typography>
      </Box>
    );
  };

  /** Responsive Header */
  const headerUI = (
    <Paper
      elevation={0}
      sx={{
        px: { xs: 1, sm: 2, md: 3, lg: 4 },
        py: { xs: 1.5, sm: 2, md: 3 },
        borderRadius: 0,
        backdropFilter: "blur(20px)",
        background: CALENDAR_COLORS.cardBackground,
        borderBottom: `1px solid ${CALENDAR_COLORS.border}`,
        display: "flex",
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: "space-between",
        alignItems: { xs: 'stretch', sm: 'center' },
        gap: { xs: 2, sm: 0 },
        position: "sticky",
        top: 0,
        zIndex: 20,
        boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
      }}
    >
      {/* LEFT SIDE */}
      <Box sx={{ 
        display: "flex", 
        alignItems: "center", 
        gap: { xs: 1, sm: 2, md: 3 },
        flexWrap: 'wrap',
      }}>
        {/* Calendar Title */}
        <Box sx={{ 
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}>
          <Typography 
            variant={isMobile ? "subtitle1" : "h6"}
            sx={{ 
              fontFamily: FONT_FAMILY,
              fontWeight: 700,
              color: CALENDAR_COLORS.textPrimary,
              fontSize: isMobile ? TYPOGRAPHY.H4.fontSize : TYPOGRAPHY.H2.fontSize,
              lineHeight: isMobile ? TYPOGRAPHY.H4.lineHeight : TYPOGRAPHY.H2.lineHeight,
            }}
          >
            {getCalendarTitle()}
          </Typography>
        </Box>

        {/* Current Date Display */}
        <Box sx={{ 
          display: "flex", 
          alignItems: "center", 
          gap: 0.5,
          background: alpha(CALENDAR_COLORS.secondary, 0.1),
          borderRadius: '8px',
          px: { xs: 1, sm: 1.5 },
          py: { xs: 0.5, sm: 0.75 },
          flexShrink: 0,
        }}>
          <CalendarMonth sx={{ 
            color: CALENDAR_COLORS.secondary, 
            fontSize: isMobile ? 16 : 20 
          }} />
          <Typography 
            variant={isMobile ? "body2" : "subtitle1"}
            sx={{
              fontFamily: FONT_FAMILY,
              fontWeight: 600,
              color: CALENDAR_COLORS.textPrimary,
              fontSize: isMobile ? TYPOGRAPHY.BODY_STANDARD.fontSize : TYPOGRAPHY.BODY_LARGE.fontSize,
            }}
          >
            {getFormattedDate()}
          </Typography>
        </Box>
      </Box>

      {/* RIGHT SIDE */}
      <Box sx={{ 
        display: "flex", 
        alignItems: "center", 
        gap: { xs: 1, sm: 2, md: 3 },
        flexWrap: 'wrap',
        justifyContent: { xs: 'space-between', sm: 'flex-end' },
      }}>
        {/* Navigation Buttons */}
        <Box sx={{ 
          display: "flex", 
          alignItems: "center", 
          gap: 0.5,
          order: { xs: 2, sm: 1 },
        }}>
          <IconButton
            onClick={() => calendarApi?.prev()}
            size={isMobile ? "small" : "medium"}
            sx={{
              color: CALENDAR_COLORS.textSecondary,
              background: alpha(CALENDAR_COLORS.primary, 0.08),
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              "&:hover": {
                background: alpha(CALENDAR_COLORS.primary, 0.15),
                transform: "translateY(-2px)",
                boxShadow: `0 4px 12px ${alpha(CALENDAR_COLORS.primary, 0.2)}`,
              },
            }}
          >
            <NavigateBefore />
          </IconButton>

          <IconButton
            onClick={() => calendarApi?.today()}
            size={isMobile ? "small" : "medium"}
            sx={{
              color: CALENDAR_COLORS.textSecondary,
              background: alpha(CALENDAR_COLORS.primary, 0.08),
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              "&:hover": { 
                background: alpha(CALENDAR_COLORS.primary, 0.15),
                transform: "translateY(-2px)",
                boxShadow: `0 4px 12px ${alpha(CALENDAR_COLORS.primary, 0.2)}`,
              },
            }}
          >
            <Today />
          </IconButton>

          <IconButton
            onClick={() => calendarApi?.next()}
            size={isMobile ? "small" : "medium"}
            sx={{
              color: CALENDAR_COLORS.textSecondary,
              background: alpha(CALENDAR_COLORS.primary, 0.08),
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              "&:hover": { 
                background: alpha(CALENDAR_COLORS.primary, 0.15),
                transform: "translateY(-2px)",
                boxShadow: `0 4px 12px ${alpha(CALENDAR_COLORS.primary, 0.2)}`,
              },
            }}
          >
            <NavigateNext />
          </IconButton>
        </Box>

        {/* Search Field */}
        <TextField
          size="small"
          placeholder="Search projects..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: <Search sx={{ 
              mr: 1, 
              color: CALENDAR_COLORS.textSecondary,
              fontSize: isMobile ? 18 : 20 
            }} />,
            sx: {
              borderRadius: '8px',
              background: alpha(CALENDAR_COLORS.cardBackground, 0.8),
              backdropFilter: "blur(10px)",
              border: `1px solid ${CALENDAR_COLORS.border}`,
              '&:hover': {
                borderColor: CALENDAR_COLORS.primary,
              },
              transition: 'all 0.3s ease',
              fontSize: isMobile ? TYPOGRAPHY.BODY_SMALL.fontSize : TYPOGRAPHY.BODY_STANDARD.fontSize,
            }
          }}
          sx={{
            width: { xs: '100%', sm: 200, md: 250, lg: 280 },
            order: { xs: 1, sm: 2 },
            minWidth: { xs: 'auto', sm: 200 },
          }}
        />

        {/* View Toggle */}
        <ToggleButtonGroup
          value={view}
          exclusive
          onChange={(e, v) => v && (setView(v), calendarApi?.changeView(v))}
          size={isMobile ? "small" : "medium"}
          sx={{
            background: alpha(CALENDAR_COLORS.cardBackground, 0.8),
            backdropFilter: "blur(10px)",
            borderRadius: '8px',
            p: 0.25,
            order: { xs: 3, sm: 3 },
            '& .MuiToggleButton-root': {
              color: CALENDAR_COLORS.textSecondary,
              border: 'none',
              borderRadius: '6px',
              px: { xs: 2, sm: 2.5 },
              py: { xs: 0.5, sm: 0.75 },
              fontWeight: 600,
              textTransform: 'none',
              fontSize: isMobile ? TYPOGRAPHY.BODY_SMALL.fontSize : TYPOGRAPHY.BODY_STANDARD.fontSize,
              '&.Mui-selected': {
                background: CALENDAR_COLORS.primary,
                color: 'white',
                boxShadow: `0 4px 14px ${alpha(CALENDAR_COLORS.primary, 0.4)}`,
              },
              '&:hover': {
                background: alpha(CALENDAR_COLORS.primary, 0.1),
                color: CALENDAR_COLORS.textPrimary,
              },
              transition: 'all 0.3s ease',
              minWidth: { xs: '60px', sm: '80px' },
            }
          }}
        >
          <ToggleButton value="timeGridWeek">
            <ViewWeek sx={{ 
              mr: 0.5, 
              fontSize: isMobile ? 16 : 18 
            }} />
            {!isMobile && "Week"}
          </ToggleButton>
          <ToggleButton value="timeGridDay">
            <Today sx={{ 
              mr: 0.5, 
              fontSize: isMobile ? 16 : 18 
            }} />
            {!isMobile && "Day"}
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
    </Paper>
  );

  /** Loading */
  if (loading)
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          background: CALENDAR_COLORS.background,
        }}
      >
        <CircularProgress 
          size={50}
          sx={{ color: CALENDAR_COLORS.primary }}
        />
      </Box>
    );

  /** PREMIUM CALENDAR CSS */
  const calendarCSS = `
    .fc {
      font-family: ${FONT_FAMILY};
      background: ${CALENDAR_COLORS.background};
    }

    .fc-toolbar { display: none !important; }

    /* Day Headers */
    .fc .fc-col-header-cell {
      background: ${CALENDAR_COLORS.cardBackground};
      backdrop-filter: blur(20px);
      border: 1px solid ${CALENDAR_COLORS.border};
      padding: ${isMobile ? '8px 4px' : '12px 8px'};
    }

    .fc .fc-col-header-cell-cushion {
      color: ${CALENDAR_COLORS.textPrimary};
      font-weight: 700;
      font-size: ${isMobile ? '0.75rem' : '0.85rem'};
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    /* Time Slots */
    .fc .fc-timegrid-slot {
      height: ${isMobile ? '45px' : '50px'} !important;
      border-bottom: 1px solid ${alpha(CALENDAR_COLORS.border, 0.3)};
      background: ${CALENDAR_COLORS.cardBackground};
    }

    .fc .fc-timegrid-slot-label {
      color: ${CALENDAR_COLORS.textSecondary};
      font-weight: 600;
      font-size: ${isMobile ? '0.7rem' : '0.75rem'};
      padding: ${isMobile ? '4px 6px' : '8px 12px'};
      border: none !important;
    }

    /* Hide half-hour slots */
    .fc .fc-timegrid-slot.fc-timegrid-slot-minor {
      border: none !important;
      display: none !important;
    }

    /* Event Style - Reduced height */
    .fc-event {
      border-radius: 4px !important;
      border: none !important;
      padding: 0 !important;
      box-shadow: 0 1px 4px rgba(0,0,0,0.1) !important;
      backdrop-filter: blur(8px);
      transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1) !important;
      margin: 1px 2px !important;
      border-left: 2px solid transparent !important;
      min-height: ${isMobile ? '28px' : '32px'} !important;
      max-height: ${isMobile ? '30px' : '34px'} !important;
      overflow: hidden !important;
      cursor: pointer !important;
    }

    .fc-event:hover {
      transform: translateY(-1px) !important;
      box-shadow: 0 3px 10px rgba(0,0,0,0.15) !important;
      z-index: 1000 !important;
    }

    /* Event Content */
    .fc-event-main {
      padding: 0 !important;
      height: 100% !important;
    }

    .fc-event-main-frame {
      display: flex !important;
      flex-direction: column !important;
      height: 100% !important;
      justify-content: center !important;
    }

    .fc-event-time {
      display: none !important;
    }

    .fc-event-title-container {
      flex: 1 !important;
      display: flex !important;
      flex-direction: column !important;
      justify-content: center !important;
    }

    /* Hide default title */
    .fc-event-title {
      display: none !important;
    }

    /* Overflow Event */
    .overflow-event {
      background: linear-gradient(135deg, ${alpha(COLORS.GREY_COLOR, 0.95)}, ${alpha(COLORS.MUTED_TEXT, 0.9)}) !important;
      border-left-color: ${COLORS.MUTED_TEXT} !important;
      font-weight: 600 !important;
      cursor: pointer !important;
      min-height: ${isMobile ? '24px' : '28px'} !important;
      max-height: ${isMobile ? '26px' : '30px'} !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
    }

    /* Stack events vertically */
    .fc-timegrid-event-harness {
      position: absolute !important;
      margin-bottom: 1px !important;
    }

    .fc-timegrid-event-harness + .fc-timegrid-event-harness {
      margin-top: ${isMobile ? '30px' : '34px'} !important;
    }

    /* Make events full width */
    .fc-timegrid-event-harness > .fc-timegrid-event {
      position: relative !important;
      width: 96% !important;
      left: 2% !important;
    }

    /* Today Highlight */
    .fc-day-today {
      background: linear-gradient(135deg, ${alpha(CALENDAR_COLORS.primary, 0.08)}, ${alpha(CALENDAR_COLORS.secondary, 0.04)}) !important;
    }

    .fc-day-today .fc-daygrid-day-number {
      background: ${CALENDAR_COLORS.primary};
      color: white !important;
      border-radius: 8px;
      width: ${isMobile ? '24px' : '28px'};
      height: ${isMobile ? '24px' : '28px'};
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: 800;
      box-shadow: 0 2px 8px ${alpha(CALENDAR_COLORS.primary, 0.3)};
    }

    /* Axis Styling */
    .fc .fc-timegrid-axis {
      background: ${CALENDAR_COLORS.cardBackground};
      border-right: 1px solid ${CALENDAR_COLORS.border};
      color: ${CALENDAR_COLORS.textSecondary};
      font-weight: 600;
      padding: ${isMobile ? '4px 6px' : '8px 12px'};
    }

    /* Grid Improvements */
    .fc .fc-timegrid-col {
      border-right: 1px solid ${CALENDAR_COLORS.border};
      background: ${CALENDAR_COLORS.cardBackground};
    }

    /* Scrollbar */
    .fc-scroller::-webkit-scrollbar {
      width: 6px;
    }

    .fc-scroller::-webkit-scrollbar-track {
      background: ${alpha(CALENDAR_COLORS.border, 0.2)};
    }

    .fc-scroller::-webkit-scrollbar-thumb {
      background: ${alpha(CALENDAR_COLORS.primary, 0.4)};
      border-radius: 3px;
    }

    /* Ensure proper event ordering */
    .fc-timegrid-event-harness {
      z-index: 1 !important;
    }

    .fc-timegrid-event-harness:hover {
      z-index: 1000 !important;
    }

    /* Responsive adjustments for day view */
    .fc-timeGridDay-view .fc-timegrid-slot {
      height: ${isMobile ? '40px' : '45px'} !important;
    }

    .fc-timeGridDay-view .fc-event {
      min-height: ${isMobile ? '25px' : '28px'} !important;
      max-height: ${isMobile ? '27px' : '30px'} !important;
    }
  `;

  return (
    <Box sx={{ 
      background: CALENDAR_COLORS.background, 
      minHeight: "100vh",
      overflow: 'hidden',
    }}>
      <style>{calendarCSS}</style>

      {headerUI}

      <Box sx={{ 
        height: { xs: "calc(100vh - 120px)", sm: "calc(100vh - 100px)", md: "calc(100vh - 88px)" },
        overflow: 'hidden',
      }}>
        <FullCalendar
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          initialView={view}
          events={calendarEvents}
          eventClick={handleEventClick}
          headerToolbar={false}
          ref={(ref) => ref && setCalendarApi(ref.getApi())}
          slotMinTime="07:00:00"
          slotMaxTime="22:00:00"
          allDaySlot={false}
          eventOverlap={false}
          nowIndicator={false}
          dayMaxEvents={true}
          eventMaxStack={isMobile ? 4 : isTablet ? 5 : 6}
          height="100%"
          eventDisplay="block"
          eventOrder="start"
          slotDuration="01:00:00"
          slotLabelFormat={{
            hour: 'numeric',
            hour12: true,
            meridiem: 'short'
          }}
          slotLabelInterval="01:00:00"
          datesSet={handleDatesSet}
          eventContent={renderEventContent}
          eventDidMount={(info) => {
            if (info.event.extendedProps.isOverflow) {
              info.el.style.borderLeftColor = COLORS.MUTED_TEXT;
              info.el.style.minHeight = isMobile ? "24px" : "28px";
              info.el.style.maxHeight = isMobile ? "26px" : "30px";
            } else {
              info.el.style.borderLeftColor = info.event.borderColor;
            }
            
            info.el.style.cursor = 'pointer';
            
            // Add tooltip on hover
            if (!info.event.extendedProps.isOverflow) {
              const { projectName, clientName, siteCheckType, engineerName, time, isVisited } = info.event.extendedProps;
              info.el.title = `
                Project: ${projectName}
                Client: ${clientName}
                Site Check: ${siteCheckType}
                Engineer: ${engineerName}
                Time: ${time}
                Status: ${isVisited ? 'Visited' : 'Scheduled'}
              `.trim();
            }
          }}
        />
      </Box>

      {/* Overlap Projects Dialog - Taller and Clean */}
      <Dialog
        open={overlapDialog.open}
        onClose={() => setOverlapDialog({ open: false, time: null, projects: [] })}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            backdropFilter: "blur(20px)",
            background: CALENDAR_COLORS.cardBackground,
            boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
            border: `1px solid ${CALENDAR_COLORS.border}`,
            maxHeight: '90vh',
            height: 'auto',
            minHeight: '70vh',
          }
        }}
      >
        <DialogTitle sx={{ 
          borderBottom: `1px solid ${CALENDAR_COLORS.border}`,
          pb: 2,
        }}>
          <Typography variant="h6" fontWeight={700} color={CALENDAR_COLORS.textPrimary} gutterBottom>
            Projects at {overlapDialog.time}
          </Typography>
          <Typography variant="body2" color={CALENDAR_COLORS.textSecondary}>
            {overlapDialog.projects.length} additional projects scheduled at this time
          </Typography>
        </DialogTitle>
        
        <DialogContent sx={{ 
          padding: { xs: 2, sm: 3 },
          maxHeight: 'calc(90vh - 120px)',
          overflow: 'auto',
        }}>
          <List sx={{ 
            pt: 0,
            '& .MuiListItem-root': {
              marginBottom: 1.5,
              borderRadius: 2,
              padding: { xs: 1.5, sm: 2 },
              background: alpha(CALENDAR_COLORS.cardBackground, 0.8),
              border: `1px solid ${CALENDAR_COLORS.border}`,
              transition: 'all 0.2s ease',
              '&:hover': {
                background: alpha(CALENDAR_COLORS.primary, 0.1),
                borderColor: CALENDAR_COLORS.primary,
                boxShadow: `0 4px 12px ${alpha(CALENDAR_COLORS.primary, 0.1)}`,
              }
            }
          }}>
            {overlapDialog.projects.map((project, index) => {
              const projectTime = project.displayDate ? format(new Date(project.displayDate), 'h:mm a') : "N/A";
              
              return (
                <ListItem
                  key={project._id}
                  sx={{
                    cursor: 'pointer',
                    padding: '16px',
                  }}
                  onClick={() => navigate(`/projects/${project._id}`)}
                >
                  <Badge
                    color="primary"
                    overlap="circular"
                    badgeContent={index + 1}
                    sx={{
                      '& .MuiBadge-badge': {
                        background: `linear-gradient(135deg, ${getProjectColor(project.index)}, ${alpha(getProjectColor(project.index), 0.7)})`,
                        color: 'white',
                        fontWeight: 700,
                        fontSize: '0.6rem',
                        minWidth: 22,
                        height: 22,
                      }
                    }}
                  >
                    <Avatar
                      sx={{
                        background: `linear-gradient(135deg, ${getProjectColor(project.index)}, ${alpha(getProjectColor(project.index), 0.7)})`,
                        mr: 2,
                        width: 48,
                        height: 48,
                        fontSize: '1rem',
                        fontWeight: 600,
                      }}
                    >
                      {project.projectName?.charAt(0)?.toUpperCase() || 'P'}
                    </Avatar>
                  </Badge>
                  
                  <ListItemText
                    primary={
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                        <Typography variant="subtitle1" fontWeight={600} color={CALENDAR_COLORS.textPrimary}>
                          {project.projectName}
                        </Typography>
                        <Chip
                          size="small"
                          label={project.visitedDate ? 'Visited' : 'Scheduled'}
                          sx={{
                            backgroundColor: project.visitedDate ? alpha(COLORS.SUCCESS, 0.1) : alpha(COLORS.WARNING, 0.1),
                            color: project.visitedDate ? COLORS.SUCCESS : COLORS.WARNING,
                            fontSize: '0.7rem',
                            height: 20,
                            fontWeight: 600,
                          }}
                        />
                      </Stack>
                    }
                    secondary={
                      <Stack spacing={1.5}>
                        {/* Project Details */}
                        <Stack spacing={0.5}>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Person sx={{ fontSize: 16, color: CALENDAR_COLORS.textSecondary, width: 20 }} />
                            <Typography variant="body2" color={CALENDAR_COLORS.textSecondary}>
                              <strong>Client:</strong> {project.clientName}
                            </Typography>
                          </Stack>
                          
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Engineering sx={{ fontSize: 16, color: CALENDAR_COLORS.textSecondary, width: 20 }} />
                            <Typography variant="body2" color={CALENDAR_COLORS.textSecondary}>
                              <strong>Engineer:</strong> {project.engineerName}
                            </Typography>
                          </Stack>
                          
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Description sx={{ fontSize: 16, color: CALENDAR_COLORS.textSecondary, width: 20 }} />
                            <Typography variant="body2" color={CALENDAR_COLORS.textSecondary}>
                              <strong>Site Check:</strong> {project.siteCheckType}
                            </Typography>
                          </Stack>
                        </Stack>

                        {/* Time */}
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Schedule sx={{ fontSize: 16, color: CALENDAR_COLORS.textSecondary, width: 20 }} />
                          <Typography variant="body2" fontWeight={600} color={CALENDAR_COLORS.primary}>
                            {projectTime}
                          </Typography>
                        </Stack>
                      </Stack>
                    }
                  />
                </ListItem>
              );
            })}
          </List>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default SiteCheckCalendar;